import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  EmployeeObjectiveCategoryAssignmentFragment,
  useEmployeeAssignedCategoriesQuery,
} from '../../../../__generated__/graphql';
import { useGoalsContext } from '../GoalsContext/GoalsContext';

/**
 * Returns the assigned categories for the employee whose goal is being created/edited.
 *
 * We use a dedicated hook with its own query instead of passing categories through
 * GoalOptions (useGoalSelectOptions) because the manager can change the employee
 * in the form's employee dropdown during goal creation. This hook watches the form's
 * employeeId and reactively fetches the correct employee's assigned categories.
 *
 * In employee (non-manager) view, the context already holds the logged-in user's
 * assigned categories, so we return those directly without an extra query.
 */
type UseGoalCategoryOptionsResult = {
  categories: readonly EmployeeObjectiveCategoryAssignmentFragment[];
  loading: boolean;
};

export const useGoalCategoryOptions = (): UseGoalCategoryOptionsResult => {
  const { isManagerView, employeeAssignedCategories, currentPeriod } =
    useGoalsContext();

  const { watch } = useFormContext();
  const formEmployeeId: string | undefined = watch('employeeId');

  const frameworkId = currentPeriod?.framework?.Id ?? null;
  const periodId = currentPeriod?.Id ?? null;

  const { data, loading } = useEmployeeAssignedCategoriesQuery({
    variables: {
      frameworkId: frameworkId!,
      employeeId: formEmployeeId!,
      periodId: periodId!,
    },
    skip: !isManagerView || !frameworkId || !periodId || !formEmployeeId,
  });

  const managerViewCategories = useMemo(
    () => data?.developmentFramework?.employeeAssignedCategories ?? [],
    [data?.developmentFramework?.employeeAssignedCategories],
  );

  if (!isManagerView) {
    return { categories: employeeAssignedCategories, loading: false };
  }

  return { categories: managerViewCategories, loading };
};
